import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Row, Segmented, Spin, Tooltip as AntTooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getTerminalHistory, type TelemetryHistoryPoint } from '../services/clarusService';
import { toNum, fmtThroughput } from '../utils/telemetryFormat';

/**
 * KAN-111: last hour of per-minute telemetry, as throughput and connection
 * quality. Both come from ONE fetch — the Clarus gateway throttles aggressively,
 * so switching views or ranges must not cost another call.
 *
 * The maximum window is the previous complete hour and cannot be widened.
 * /telemetry/history takes no time range: requesting windows 6 hours and 7 days
 * back returned the same previous-complete-hour data, on every terminal tested.
 * /telemetry/average accepts a range but returns a single aggregate, not a
 * series. Longer history therefore requires storing our own. Within the hour we
 * do have, the 15-minute range is a client-side slice of the same data.
 *
 * `layout`: "toggle" (default) shows one chart with a Throughput/Quality switch;
 * "split" shows both charts side by side with a 15m / 1h range control.
 */

interface Props {
  deviceId: string;
  /**
   * Bumped by the page's single refresh control. The chart has no button of
   * its own — one terminal, one refresh, everything reloads together.
   */
  refreshToken?: number;
  layout?: 'toggle' | 'split';
}

type View = 'throughput' | 'quality';
type Range = '15m' | '1h';

interface ChartPoint {
  time: string;
  ts: number;
  downlink: number | null;
  uplink: number | null;
  latency: number | null;
  signal: number | null;
  drop: number | null;
}

/**
 * Clarus sends signalQuality and pingDropRateAvg as 0–1 fractions; the UI talks
 * in percent. Points arrive newest-first, so they are sorted ascending or the
 * chart reads backwards.
 */
export const toChartData = (points: TelemetryHistoryPoint[]): ChartPoint[] =>
  points
    .slice()
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map(p => {
      const signal = toNum(p.signalQuality);
      const drop = toNum(p.pingDropRateAvg);
      const d = new Date(p.timestamp);
      return {
        time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ts: d.getTime(),
        downlink: toNum(p.downlinkThroughput),
        uplink: toNum(p.uplinkThroughput),
        latency: toNum(p.pingLatencyMsAvg),
        signal: signal === null ? null : signal * 100,
        drop: drop === null ? null : drop * 100,
      };
    });

/**
 * Ping drop is zero for most terminals most of the time. Plotting a flat zero
 * line adds a legend entry and no information, so the series only appears when
 * the terminal actually dropped packets in the window.
 *
 * Obstruction is deliberately absent from both views: observed values move
 * between 0.0044% and 0.0046%, i.e. noise in the fourth decimal.
 */
export const hasPacketLoss = (data: ChartPoint[]): boolean =>
  data.some(d => d.drop !== null && d.drop > 0);

const throughputChart = (data: ChartPoint[]) => (
  <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="time" minTickGap={40} tick={{ fontSize: 12 }} />
    <YAxis
      tick={{ fontSize: 12 }}
      label={{ value: 'Mbps', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
    />
    <Tooltip formatter={(value: number | string) => fmtThroughput(value)} />
    <Legend />
    <Line
      type="monotone"
      dataKey="downlink"
      name="Download"
      stroke="#1677ff"
      dot={false}
      connectNulls
      isAnimationActive={false}
    />
    <Line
      type="monotone"
      dataKey="uplink"
      name="Upload"
      stroke="#52c41a"
      dot={false}
      connectNulls
      isAnimationActive={false}
    />
  </LineChart>
);

const qualityChart = (data: ChartPoint[]) => {
  const showDrop = hasPacketLoss(data);
  // Latency (ms) and signal (%) share no scale, so they get their own axes.
  return (
    <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" minTickGap={40} tick={{ fontSize: 12 }} />
      <YAxis
        yAxisId="ms"
        tick={{ fontSize: 12 }}
        label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
      />
      <YAxis
        yAxisId="pct"
        orientation="right"
        domain={[0, 100]}
        tick={{ fontSize: 12 }}
        label={{ value: '%', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
      />
      <Tooltip
        formatter={(value: number | string, name: string) => {
          const n = toNum(value);
          if (n === null) return '—';
          return name === 'Latency' ? `${n.toFixed(0)} ms` : `${n.toFixed(1)} %`;
        }}
      />
      <Legend />
      <Line
        yAxisId="ms"
        type="monotone"
        dataKey="latency"
        name="Latency"
        stroke="#fa8c16"
        dot={false}
        connectNulls
        isAnimationActive={false}
      />
      {/* Signal sits at 100% and steps down, so stepAfter tells the truth
          about when the drop happened where a smoothed line would not. */}
      <Line
        yAxisId="pct"
        type="stepAfter"
        dataKey="signal"
        name="Signal"
        stroke="#722ed1"
        dot={false}
        connectNulls
        isAnimationActive={false}
      />
      {showDrop && (
        <Line
          yAxisId="pct"
          type="monotone"
          dataKey="drop"
          name="Packet loss"
          stroke="#ff4d4f"
          dot={false}
          connectNulls
          isAnimationActive={false}
        />
      )}
    </LineChart>
  );
};

const TelemetryHistoryChart: React.FC<Props> = ({
  deviceId,
  refreshToken = 0,
  layout = 'toggle',
}) => {
  const [data, setData] = useState<ChartPoint[]>([]);
  const [view, setView] = useState<View>('throughput');
  const [range, setRange] = useState<Range>('1h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const { points } = await getTerminalHistory(deviceId, signal);
        if (signal?.aborted) return;
        setData(toChartData(points || []));
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load telemetry history');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [deviceId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshToken]);

  // 15m is a client-side slice of the same hour of data — no extra fetch.
  const sliced = useMemo(() => {
    if (layout !== 'split' || range !== '15m' || data.length === 0) return data;
    const cutoff = data[data.length - 1].ts - 15 * 60 * 1000;
    return data.filter(d => d.ts >= cutoff);
  }, [data, layout, range]);

  const help = (
    <AntTooltip
      title={
        'Clarus exposes only the previous complete hour of per-minute telemetry, so this is the widest window available. ' +
        'A terminal that was offline for the hour reports no points.\n\n' +
        'Quality shows latency and signal — a slow-feeling connection is usually one of those rather than throughput, ' +
        'which mostly reflects whether anyone happens to be downloading.'
      }
    >
      <InfoCircleOutlined style={{ marginLeft: 8, color: 'rgba(0,0,0,0.45)' }} />
    </AntTooltip>
  );

  const spinner = (
    <div style={{ textAlign: 'center', padding: 32 }}>
      <Spin />
    </div>
  );
  const errorState = (
    <Alert
      type="error"
      showIcon
      message="Could not load telemetry history"
      description={error}
      action={
        <Button size="small" onClick={() => load()}>
          Retry
        </Button>
      }
    />
  );
  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No telemetry reported in the last complete hour"
    />
  );
  const state =
    loading && data.length === 0
      ? spinner
      : error
        ? errorState
        : data.length === 0
          ? emptyState
          : null;

  if (layout === 'split') {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
            Per-minute telemetry{help}
          </span>
          <Segmented
            size="small"
            value={range}
            onChange={v => setRange(v as Range)}
            options={[
              { label: '15m', value: '15m' },
              { label: '1h', value: '1h' },
            ]}
          />
        </div>
        {state ?? (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                Throughput · Mbps
              </div>
              <ResponsiveContainer width="100%" height={240}>
                {throughputChart(sliced)}
              </ResponsiveContainer>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Quality</div>
              <ResponsiveContainer width="100%" height={240}>
                {qualityChart(sliced)}
              </ResponsiveContainer>
            </Col>
          </Row>
        )}
      </div>
    );
  }

  return (
    <Card
      size="small"
      title={
        <>
          Last hour
          {help}
        </>
      }
      extra={
        <Segmented
          size="small"
          value={view}
          onChange={v => setView(v as View)}
          options={[
            { label: 'Throughput', value: 'throughput' },
            { label: 'Quality', value: 'quality' },
          ]}
        />
      }
    >
      {state ?? (
        <ResponsiveContainer width="100%" height={260}>
          {view === 'throughput' ? throughputChart(data) : qualityChart(data)}
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default TelemetryHistoryChart;
